import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowRight, Info } from "lucide-react";

export const Reveal = ({ children, delay = 0, className = "" }) => (
  <motion.div
    className={className}
    initial={{ opacity: 0, y: 24 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: "-60px" }}
    transition={{ duration: 0.55, delay, ease: [0.22, 1, 0.36, 1] }}
  >
    {children}
  </motion.div>
);

export const SectionHeading = ({ eyebrow, title, children, className = "" }) => (
  <div className={`max-w-3xl ${className}`}>
    {eyebrow && <p className="eyebrow mb-4">{eyebrow}</p>}
    <h2 className="font-display text-4xl uppercase leading-[0.95] sm:text-5xl lg:text-6xl">{title}</h2>
    {children && <p className="mt-5 text-base text-slate-300 md:text-lg">{children}</p>}
  </div>
);

export const Cta = ({ to, children, variant = "primary", testid, className = "" }) => {
  const styles = {
    primary: "bg-[#00E5FF] text-[#0A192F] hover:bg-[#5cefff]",
    accent: "bg-[#FF5722] text-white hover:bg-[#ff7a4f]",
    ghost: "border border-white/25 text-white hover:border-[#00E5FF] hover:text-[#00E5FF]",
  };
  return (
    <Link
      to={to}
      data-testid={testid}
      className={`group inline-flex items-center gap-2 rounded-full px-6 py-3 text-sm font-bold uppercase tracking-wider transition-colors duration-300 ${styles[variant]} ${className}`}
    >
      {children}
      <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
    </Link>
  );
};

export const PageHero = ({ eyebrow, title, children, image = "/images/hero.jpg", testid }) => (
  <section data-testid={testid} className="grain relative overflow-hidden border-b border-white/10 pb-16 pt-36 sm:pb-20 sm:pt-44">
    <img src={image} alt="" aria-hidden="true" className="absolute inset-0 h-full w-full object-cover opacity-30" />
    <div className="absolute inset-0 bg-gradient-to-t from-[#0A192F] via-[#0A192F]/80 to-[#0A192F]/50" />
    <div className="container-x relative">
      {eyebrow && <p className="eyebrow mb-4">{eyebrow}</p>}
      <h1 className="max-w-4xl font-display text-4xl uppercase leading-[0.95] sm:text-5xl lg:text-6xl">{title}</h1>
      {children && <div className="mt-6 max-w-2xl text-base text-slate-300 md:text-lg">{children}</div>}
    </div>
  </section>
);

export const DemoBadge = ({ label = "Demo content" }) => (
  <span data-testid="demo-badge" className="inline-flex items-center gap-1 rounded-sm border border-[#FACC15]/50 bg-[#FACC15]/10 px-2 py-0.5 text-[10px] font-bold uppercase tracking-widest text-[#FACC15]">
    {label}
  </span>
);

export const StatusPill = ({ children, tone = "soon" }) => {
  const tones = {
    soon: "border-[#FF5722]/50 bg-[#FF5722]/10 text-[#FF8a65]",
    ok: "border-[#00E5FF]/50 bg-[#00E5FF]/10 text-[#00E5FF]",
    mute: "border-white/20 bg-white/5 text-slate-300",
  };
  return <span className={`inline-flex items-center rounded-full border px-3 py-1 text-[10px] font-bold uppercase tracking-[0.18em] ${tones[tone]}`}>{children}</span>;
};

export const Notice = ({ children, testid }) => (
  <div data-testid={testid} className="flex gap-3 rounded-md border border-[#FACC15]/30 bg-[#FACC15]/5 p-4 text-sm text-slate-200">
    <Info className="mt-0.5 h-4 w-4 shrink-0 text-[#FACC15]" />
    <div>{children}</div>
  </div>
);

export const EmptyState = ({ title, children, testid, icon: Icon }) => (
  <div data-testid={testid} className="stripe-bg flex flex-col items-start gap-3 rounded-md border border-dashed border-white/15 p-8 sm:p-12">
    {Icon && <Icon className="h-8 w-8 text-[#00E5FF]" />}
    <h3 className="font-display text-2xl uppercase sm:text-3xl">{title}</h3>
    {children && <p className="max-w-xl text-sm text-slate-400">{children}</p>}
  </div>
);

export const Crest = ({ number, color = "#00E5FF", logo, name, size = "md" }) => {
  const dims = size === "lg" ? "h-28 w-24" : size === "sm" ? "h-12 w-10" : "h-20 w-[4.5rem]";
  if (logo) return <img src={logo} alt={`${name} logo`} loading="lazy" className={`${dims} object-contain`} />;
  return (
    <svg viewBox="0 0 80 92" className={dims} role="img" aria-label={`${name || "Franchise"} placeholder crest`}>
      <path d="M40 2 76 14v30c0 24-16 38-36 46C20 82 4 68 4 44V14Z" fill="#0A192F" stroke={color} strokeWidth="2.5" />
      <path d="M40 12 66 21v22c0 17-11 28-26 34-15-6-26-17-26-34V21Z" fill={color} opacity=".12" />
      <path d="M14 58 66 30" stroke={color} strokeWidth="1.5" opacity=".5" />
      <text x="40" y="54" textAnchor="middle" fontFamily="Anton, Impact" fontSize="26" fill="#fff">{String(number).padStart(2, "0")}</text>
    </svg>
  );
};
